import Activity, { type ActivityGroup } from '../../model/Activity'

export type { ActivityGroup }

export function computeRms(activity: Activity, value: number): number {
  if (!activity.requiresAmount) return Math.round(activity.unitConversion)
  if (!value || value <= 0) return 0
  return Math.round(value * activity.unitConversion)
}

export function getQuickAmounts(activity: Activity): number[] {
  if (!activity.requiresAmount) return []
  if (activity.unit === 'mi') return [5, 10, 15, 26.2]
  if (activity.unit === 'km') return [3, 5, 10, 21.1]
  if (activity.unit === 'min') return [30, 45, 60, 90]
  return [1, 2, 3, 5]
}

const DISTANCE_WORDS: Record<string, readonly [string, string]> = {
  km: ['kilometer', 'kilometers'],
  m: ['meter', 'meters'],
  mi: ['mile', 'miles'],
}

export function getFixedLabel(activity: Activity): string {
  const measured = activity.description.match(/^(\d+(?:\.\d+)?)\s+(km|m|mi)\s+=/i)
  if (measured) {
    const quantity = Number(measured[1])
    const words = DISTANCE_WORDS[measured[2]!.toLowerCase()]
    if (words && Number.isFinite(quantity)) {
      const amount = Number.isInteger(quantity) ? String(quantity) : measured[1]!
      return `${amount} ${quantity === 1 ? words[0] : words[1]}`
    }
  }
  return `1 ${activity.unit || 'class'}`
}

export function rmLabel(rms: number): string {
  const rounded = Math.round(rms)
  return `${rounded} RM${rounded === 1 ? '' : 's'}`
}

export function gateCutWarning(cutRms: number, creditedRms: number): string | null {
  if (Math.round(cutRms) <= 0) return null
  return `${rmLabel(cutRms)} won't be logged because of the gate, so only ${rmLabel(creditedRms)} will count.`
}

export function getLoggedUnitWord(unit: string): string {
  if (unit === 'min') return 'minutes'
  if (unit === 'mi' || unit === 'mile') return 'miles'
  return unit
}

export function getLogVerb(activity: Activity): string {
  const title = activity.title.toLowerCase()
  if (title.includes('bike')) return 'biking'
  if (title.includes('run') || title.includes('walk')) return 'run/walk'
  if (title.includes('swim')) return 'swimming'
  if (title.includes('row')) return 'rowing'
  if (title.includes('hike')) return 'a hike'
  if (title.includes('yoga')) return 'yoga'
  if (title.includes('pickleball')) return 'pickleball'
  if (title.includes('class')) return 'a workout class'
  if (title.includes('partner')) return `partner ${title.replace('partner ', '')}`
  return title
}

export function groupActivities(activities: Activity[]): Record<ActivityGroup, Activity[]> {
  const groups: Record<ActivityGroup, Activity[]> = {
    primary: [],
    everyday: [],
    partner: [],
    bonus: [],
  }

  for (const activity of activities) {
    groups[activity.group].push(activity)
  }

  return groups
}

export function orderActivities(activities: Activity[]): Activity[] {
  return [...activities].sort((a, b) => {
    const aOrder = a.order ?? Infinity
    const bOrder = b.order ?? Infinity
    if (aOrder !== bOrder) return aOrder - bOrder
    return a.title.localeCompare(b.title)
  })
}

export const VICTORY_QUIPS = [
  'Holy schnikes, you just crushed that.',
  'No one thought you could do it, and yet, you just did it.',
  'NOIIIICCCE!!!',
  'Sheesh, nice work.',
  'Boom goes the dynamite.',
  'High five! Nice work.',
  "I'd say 'I'm impressed,' but I'm too busy being amazed.",
  "Not all heroes wear capes, unless you're wearing a cape.",
  'You just logged that like a BOSS.',
  "Grab a snack yo, you've earned it.",
  'Kaboom diggity.',
  'DANG! Nice work.',
  'Pizza. You just earned pizza.',
  'Beast mode = you.',
  'Legendary.',
  'Teach me your ways, grasshopper.',
  'Mic drop.',
  'Ooohhhhh kay! Skadoooosh!!',
  'Two words: Sweet. Yo.',
  'Savage. Classy. Bougie. And, might I add, ratchet?',
  'HQ is slow clapping in awe.',
  'Workout? More like work of art! AMIRIGHT?!',
  'Crushed it. Then smashed it.',
  'Shut the front door with that workout. Shut IT!',
  'You. Are. Epic.',
  '"Awesome" in the dictionary = your photo.',
  'Did you trade coffee for ROCKET FUEL this morning?!',
  "Someone call Elphaba - you're defying gravity.",
  'True statement: You rock.',
  "We'll notify Sports Center.",
  'Cue: Rocky Theme',
  "I can't even with you right now.",
  'Wow. Just wow.',
  'You, my friend, amaze me.',
  'You rocked it, then logged it.',
  'Are you even real? Asking for a friend.',
  'Brb, nominating you for the Hall of Fame.',
  'Nice work, Chief.',
  'That was a beaut, Clark.',
  'You came, you saw, you, well, you know what you did.',
  "That workout didn't stand a chance.",
  'Casual excellence detected.',
  'We love to see it. LOVE TO SEE IT!',
  'Critics agree; 100% Certified Fresh effort',
  "That'll play.",
  'You woke up and chose dominance.',
  'Respectfully…wow.',
  'Textbook execution. No notes.',
  'You made that look suspiciously easy.',
  'Put that one on the highlight reel.',
  'Clean. Strong. Intentional.',
  'And that is how it is done, my friend.',
  'That effort? Immaculate.',
  'You vs. that workout = no contest.',
  'Flex.',
  '10/10 would recommend.',
  'Momentum looks good on you.',
  "You're making it hard to stay humble for you.",
  'Another day, another RM logged. Boom.',
  'Progress just felt that.',
  'No shortcuts taken. Love to see it.',
  'That workout just learned something.',
  'You earned every bit of that.',
  "You're stacking dubs now.",
  "Yep. That'll do.",
  'Hair toss. Check your nails. Baby, how ya feeling?',
  'I love the smell of gains in the morning.',
  "We're gonna need a bigger leaderboard.",
  'Say less.',
  'You ate. No crumbs.',
]

export function randomQuip(): string {
  return VICTORY_QUIPS[Math.floor(Math.random() * VICTORY_QUIPS.length)]
}
